// src/Components/Chart_Support_Files/chartStudyLoader.js

import { indexIndicators } from './chartIndicatorConfig';

const DEFAULT_STUDY_RETRIES = 8;
const DEFAULT_STUDY_RETRY_MS = 350;

function hasStudy(chart, indicator) {
  try {
    return chart.getAllStudies().some((study) => (
      study.name === indicator ||
      study.shortName === indicator ||
      study.description === indicator
    ));
  } catch {
    return false;
  }
}

function indicatorConfigFor(symbol) {
  const key = symbolKey(symbol);
  return indexIndicators[symbol] || indexIndicators[key];
}

function symbolKey(symbol) {
  return String(symbol || '').split(':').pop();
}

export function loadDefaultIndicators({
  chart,
  symbol,
  dynamicLevels,
  topOptions,
  greekExposures,
  attempt = 0,
  signal,
}) {
  const config = indicatorConfigFor(symbol);

  if (!config?.defaultIndicators) {
    return () => {};
  }

  const timers = new Set();
  let cancelled = false;
  const cancel = () => {
    cancelled = true;
    for (const timer of timers) clearTimeout(timer);
    timers.clear();
  };
  if (signal) {
    if (signal.aborted) return cancel;
    signal.addEventListener('abort', cancel, { once: true });
  }

  const run = (currentAttempt) => {
    if (cancelled || signal?.aborted) return;
    const missing = [];

    config.defaultIndicators.forEach((indicator) => {
      if (hasStudy(chart, indicator)) return;

      try {
        chart.createStudy(indicator, false, false, {
          dynamicLevels,
          topOptions,
          greekExposures,
        });
      } catch (error) {
        missing.push(indicator);
        if (!cancelled && !signal?.aborted) {
          console.warn(`Failed to create study ${indicator}:`, error);
        }
      }
    });

    if (missing.length > 0 && currentAttempt < DEFAULT_STUDY_RETRIES) {
      const retryTimer = setTimeout(() => {
        timers.delete(retryTimer);
        run(currentAttempt + 1);
      }, DEFAULT_STUDY_RETRY_MS);
      timers.add(retryTimer);
    }
  };

  const timer = setTimeout(() => {
    timers.delete(timer);
    run(attempt);
  }, 0);
  timers.add(timer);

  return cancel;
}

export function clearAllStudies(chart) {
  const studies = chart.getAllStudies();

  studies.forEach((study) => {
    try {
      chart.removeEntity(study.id);
    } catch (error) {
      console.warn(`Failed to remove study ${study.name}:`, error);
    }
  });
}

export function attachSymbolChangeHandler({
  chart,
  dynamicLevels,
  topOptions,
  greekExposures,
  getStudyInputs,
}) {
  const disposers = new Set();
  let previousSymbol = symbolKey(chart.symbol());
  const handler = () => {
    const currentSymbol = chart.symbol();
    const currentSymbolKey = symbolKey(currentSymbol);
    if (currentSymbolKey === previousSymbol) return;
    previousSymbol = currentSymbolKey;

    const timer = setTimeout(() => {
      disposers.delete(timerDisposer);
      try {
        clearAllStudies(chart);
        const currentInputs = getStudyInputs?.() || {
          dynamicLevels,
          topOptions,
          greekExposures,
        };

        const disposeLoad = loadDefaultIndicators({
          chart,
          symbol: currentSymbol,
          ...currentInputs,
        });
        disposers.add(disposeLoad);
      } catch (error) {
        console.warn('Error during symbol change handler:', error);
      }
    }, 0);
    const timerDisposer = () => clearTimeout(timer);
    disposers.add(timerDisposer);
  };

  chart.onSymbolChanged().subscribe(null, handler);

  return () => {
    try {
      chart.onSymbolChanged().unsubscribe(null, handler);
    } catch { /* non-fatal TradingView API drift */ }
    for (const dispose of disposers) dispose();
    disposers.clear();
  };
}
